import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./Epaper.css";
import { FaRegCalendarAlt } from "react-icons/fa"; // 👈 Add this at top with other imports


const API_URL = "https://api.shabdtoday.com/api/home/";
const BASE_URL = "https://api.shabdtoday.com/";

const truncateText = (text, maxLength) =>
  text.length > maxLength ? text.substring(0, maxLength) : text;

const stripHtmlTags = (html, maxLength) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return truncateText(tempDiv.textContent || tempDiv.innerText || "", maxLength);
};

const formatDateInHindi = (dateString) => {
  const date = new Date(dateString);
  const months = [
    "जनवरी",
    "फरवरी",
    "मार्च",
    "अप्रैल",
    "मई",
    "जून",
    "जुलाई",
    "अगस्त",
    "सितंबर",
    "अक्टूबर",
    "नवंबर",
    "दिसंबर",
  ];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

function Epaper() {
  const [bhopalEdition, setBhopalEdition] = useState([]);
  const [betulEdition, setBetulEdition] = useState([]);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const navigate = useNavigate();

  const calendarRef = useRef(null);

  // Close calendar if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        !event.target.classList.contains("datepicker-button")
      ) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchEpaperByDate = (date) => {
    const formattedDate = date.toISOString().split("T")[0];
    axios
      .get(`${API_URL}getEpaperByDate?date=${formattedDate}`)
      .then((res) => {
        const data = res.data.dataEpapersPDF || [];
        setBhopalEdition(data.filter((item) => item.title.includes("Bhopal Edition")));
        setBetulEdition(data.filter((item) => item.title.includes("Betul Edition")));
      })
      .catch((err) =>
        setError("Error fetching e-paper for the selected date: " + err.message)
      );
  };

  useEffect(() => {
    fetchEpaperByDate(selectedDate);
  }, [selectedDate]);

  if (error)
    return (
      <div className="text-center text-danger mt-4">
        Error: {error}
      </div>
    );

  return (
    <div style={{ backgroundColor: "white", minHeight: "100vh" }}>
      <HelmetProvider>
        <Helmet>
          <title>शब्द टुडे | ई-पेपर</title>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1"
          />
        </Helmet>

        {/* Header with Logo and DatePicker Button on right */}
        <div
          className="d-flex justify-content-between align-items-center px-3"
          style={{ marginTop: "20px", color: "white" }}
        >
          <Link to="/">
            <img
              src="/design/images/logo-mike1.png"
              alt="शब्द टुडे Logo"
              width={120}
              height={50}
            />
          </Link>

          {/* DatePicker Button */}
          <div ref={calendarRef} style={{ position: "relative" }}>
            <button
              type="button"
              className="btn datepicker-button d-flex align-items-center"
              onClick={() => setShowCalendar((prev) => !prev)}
              style={{
                backgroundColor: "#dc143c",      // crimson background
                borderColor: "#dc143c",          // border same as background
                color: "white",                  // white text
                fontWeight: "500",
              }}
            >
              {selectedDate.toLocaleDateString("hi-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
              <FaRegCalendarAlt
                size={18}
                color="white" // icon also white
                style={{ marginLeft: "8px", cursor: "pointer" }}
                title="Open calendar"
              />
            </button>


            {showCalendar && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 5px)",
                  right: 0,
                  zIndex: 9999,
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  backgroundColor: "white",
                  borderRadius: "4px",
                }}
              >
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => {
                    setSelectedDate(date);
                    setShowCalendar(false);
                  }}
                  inline
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            )}
          </div>
        </div>

        <hr style={{ borderColor: "white" }} />
      </HelmetProvider>

      {/* Content Section */}
      <div className="container bg-white py-0">
        <div className="row">
          {/* Bhopal Edition */}
          <div className="col-md-6 mb-4">
            <h2 className="text-center mb-4 text-dark">Bhopal Edition</h2>
            {bhopalEdition.length > 0 ? (
              bhopalEdition.map((newsItem) => (
                <EditionCard key={newsItem.id} newsItem={newsItem} />
              ))
            ) : (
              <p className="text-center text-muted">
                No Bhopal edition found for the selected date.
              </p>
            )}
          </div>

          {/* Betul Edition */}
          <div className="col-md-6 mb-4">
            <h2 className="text-center mb-4 text-dark">Betul Edition</h2>
            {betulEdition.length > 0 ? (
              betulEdition.map((newsItem) => (
                <EditionCard key={newsItem.id} newsItem={newsItem} />
              ))
            ) : (
              <p className="text-center text-muted">
                No Betul edition found for the selected date.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const EditionCard = ({ newsItem }) => {
  const navigate = useNavigate();

  return (
    <div className="card shadow-sm">
      <div className="card-body text-center">
        {newsItem.pdfUrl && (
          <div
            className="iframe-container"
            style={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden" }}
            onClick={() => navigate(`/pdf/${newsItem.id}`)}
          >
            <iframe
              src={BASE_URL + newsItem.pdfUrl + "#toolbar=0&navpanes=0&scrollbar=0"}
              width="100%"
              height="200"
              title="E-paper PDF"
              style={{ border: "none", pointerEvents: "none" }}
            />
          </div>
        )}
        <h4>
          {stripHtmlTags(newsItem.title, 30)} - {formatDateInHindi(newsItem.date)}
        </h4>
      </div>
    </div>
  );
};

export default Epaper;
